// src/components/RealFuelTrendChart.tsx - UPDATED TO HANDLE BOTH FORMATS

import React, { useState } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';

interface Props {
  data: any;
}

// ✅ Custom Tooltip with vehicle breakdown
const CustomFuelTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const MAX_VEHICLES_SHOWN = 5;

  // Sort vehicles by consumed fuel (highest first)
  const sortedVehicles = data.vehicles 
    ? [...data.vehicles].sort((a: any, b: any) => (b.consumedLiters || 0) - (a.consumedLiters || 0))
    : [];
  
  const topVehicles = sortedVehicles.slice(0, MAX_VEHICLES_SHOWN);
  const remainingCount = sortedVehicles.length - MAX_VEHICLES_SHOWN;

  return (
    <Box
      sx={{
        bgcolor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        p: 1.5,
        boxShadow: 2,
        minWidth: 240,
        maxWidth: 350,
        maxHeight: 400,
        overflow: 'auto',
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        {label}
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Consumed
          </Typography>
          <Typography variant="body2" color="#ff9800" fontWeight="600">
            {data.consumedLiters?.toFixed(1) || 0} L
          </Typography>
        </Box>
      </Box>

      {topVehicles.length > 0 && (
        <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 1, mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {sortedVehicles.length > MAX_VEHICLES_SHOWN 
              ? `Top ${MAX_VEHICLES_SHOWN} Consumers:` 
              : 'Vehicle Breakdown:'}
          </Typography>
          {topVehicles.map((vehicle: any, idx: number) => (
            <Box 
              key={idx} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                py: 0.5,
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    display: 'block',
                    lineHeight: 1.3,
                  }}
                >
                  {vehicle.plateNumber || vehicle.imei || 'Unknown'}
                </Typography>
                {(vehicle.make || vehicle.model) && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.65rem',
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    {vehicle.make} {vehicle.model}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography 
                  variant="caption" 
                  color="#ff9800"
                  sx={{ 
                    fontWeight: 600,
                    display: 'block',
                  }}
                >
                  {vehicle.consumedLiters?.toFixed(1) || 0} L
                </Typography>
                {vehicle.addedLiters > 0 && (
                  <Typography 
                    variant="caption" 
                    color="#4caf50"
                    sx={{ 
                      fontSize: '0.65rem',
                      display: 'block',
                    }}
                  >
                    +{vehicle.addedLiters?.toFixed(1)} L
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
          
          {remainingCount > 0 && (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                py: 0.5,
                mt: 0.5,
                borderTop: '1px dashed #e0e0e0',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                + {remainingCount} more vehicle{remainingCount > 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {data.vehicleCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
          {data.vehicleCount || sortedVehicles.length} vehicle{(data.vehicleCount || sortedVehicles.length) > 1 ? 's' : ''} active
        </Typography>
      )}
    </Box>
  );
};

const RealFuelTrendChart: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  console.log('⛽ Fuel Chart Received:', {
    type: typeof data,
    isArray: Array.isArray(data),
    length: Array.isArray(data) ? data.length : 0,
    hasData: !!data,
    sample: Array.isArray(data) ? data[0] : data
  });

  // ✅ FIXED: Handle both data formats (array or object with byDate)
  let fuelData: any[] = [];
  
  if (Array.isArray(data)) {
    fuelData = data;
  } else if (data?.byDate && Array.isArray(data.byDate)) {
    fuelData = data.byDate;
  } else if (data && typeof data === 'object') {
    // Fallback: try to extract array from object
    const possibleArrays = Object.values(data).filter(Array.isArray);
    fuelData = possibleArrays.length > 0 ? possibleArrays[0] : [];
  }

  if (!fuelData || fuelData.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary" variant="body2" textAlign="center">
          No fuel consumption data available
        </Typography>
      </Box>
    );
  }

  // Transform data for chart
  const chartData = fuelData
    .map((item: any) => {
      // Handle both date formats
      const itemDate = item.date || item._id || new Date().toISOString();
      
      return {
        date: new Date(itemDate).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }),
        fullDate: itemDate,
        consumedLiters: Math.round((item.totalConsumedLiters || item.fuelConsumedLiters || 0) * 10) / 10,
        addedLiters: Math.round((item.totalAddedLiters || item.fuelAddedLiters || 0) * 10) / 10,
        vehicles: item.vehicles || [],
        vehicleCount: item.vehicles?.length || 0,
      };
    })
    .filter(item => item.consumedLiters > 0 || item.addedLiters > 0) // Only show days with data
    .slice(-7); // Last 7 days

  console.log('📊 Transformed Chart Data:', chartData);

  if (chartData.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary" variant="body2" textAlign="center">
          No fuel consumption recorded in this period
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 200 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart 
          data={chartData}
          margin={{ 
            top: 10, 
            right: isMobile ? 10 : 30, 
            left: isMobile ? 0 : 20, 
            bottom: isMobile ? 20 : 5 
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            axisLine={false} 
            tickLine={false}
            tick={{ fontSize: isMobile ? 9 : 12 }}
            angle={isMobile ? -45 : 0}
            textAnchor={isMobile ? 'end' : 'middle'}
            height={isMobile ? 60 : 30}
          />
          <YAxis 
            axisLine={false} 
            tickLine={false} 
            tick={{ fontSize: isMobile ? 10 : 12 }}
            width={isMobile ? 40 : 60}
            label={{ value: 'Liters', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
          />
          <Tooltip content={<CustomFuelTooltip />} />
          <Line 
            type="monotone" 
            dataKey="consumedLiters" 
            stroke="#ff9800" 
            strokeWidth={isMobile ? 2 : 3}
            dot={{ r: isMobile ? 2 : 3 }}
            name="Consumed"
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RealFuelTrendChart;